<template>
  <!-- AB#21842 — a bare page (the Ops Command Center wall): no shell around it. -->
  <router-view v-if="route.meta.bare" />

  <!-- Public standalone pages (e.g. customer tracker) render without the
       admin shell — no sidebar/header (US #18826, task 18978). -->
  <div v-else-if="isPublicRoute" class="h-screen w-full overflow-y-auto">
    <router-view />
  </div>

  <div v-else class="h-screen w-full flex flex-col">
    <ImpersonationBanner />
    <div
      class="main flex-1 min-h-0 w-full bg-app flex overflow-y-auto overflow-x-visible overflow-visible"
    >
      <Loader :loading="globalLoader" />
      <!-- Sidebar -->
      <Transition name="sidebar-slide overflow-visible">
        <Sidebar
          :navigation="navigation"
          class="transition-all duration-200 h-full overflow-visible"
          :class="mobileDevice ? '' : sidebarOpen ? 'w-[232px]' : 'w-[64px]'"
        />
      </Transition>
      <div
        class="flex flex-col border-1 flex-1 h-full min-h-0 overflow-visible scroll-hidden"
        :class="{ mobileDevice: mobileDevice, desktop: !mobileDevice }"
        :style="
          mobileDevice
            ? { width: '100vw' }
            : { width: sidebarOpen ? 'calc(100vw - 232px)' : 'calc(100vw - 64px)' }
        "
      >
        <Header />
        <div
          class="h-full flex-1 w-full mx-auto heightClass overflow-y-auto min-h-0 overflow-visible"
        >
          <router-view />
        </div>
      </div>
    </div>
    <!-- AB#21684 — the portal's own Ask Atlas launcher; Zendesk's bubble is hidden. -->
    <AtlasLauncher />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Header from './components/Header.vue';
import ImpersonationBanner from './components/ImpersonationBanner.vue';
import Sidebar from './components/Sidebar.vue';
import { useMainStore } from './store';
import Loader from './components/Shared/Loader.vue';
import { getImpersonation } from './composables/impersonation';
import { startZendeskWidget } from './composables/useZendeskWidget';
import AtlasLauncher from '@/components/Shared/AtlasLauncher.vue';

const store = useMainStore();
const route = useRoute();
const { navigation, mobileDevice, sidebarOpen, globalLoader, user, role } = storeToRefs(store);

/** Public/standalone routes render without the admin sidebar + header. */
const isPublicRoute = computed<boolean>(() => route.meta?.public === true);
const screenWidth = ref(window.innerWidth);
const screenHeight = ref(window.innerHeight);

const updateScreenSize = () => {
  screenWidth.value = window.innerWidth;
  screenHeight.value = window.innerHeight;
  mobileDevice.value = window.innerWidth <= 767;
};

/**
 * Support widget (AB#21165).
 *
 * Started from the shell rather than a page so it is there wherever the user
 * happens to be, and started from a watcher rather than onMounted because the
 * shell mounts before `authuser` has answered — the whole point is that the
 * contact form already knows who is asking. First user wins; `startZendeskWidget`
 * returns false when there is no key configured.
 *
 * In an impersonated session the ticket is attributed to the real admin, not the
 * account being viewed through — `getImpersonation()` is who that is.
 */
let zendeskStarted = false;
watch(
  user,
  (value) => {
    if (zendeskStarted || !value) return;
    zendeskStarted = startZendeskWidget(value, role.value, getImpersonation()?.by);
  },
  { immediate: true }
);

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
});
</script>

<style lang="scss">
.heightClass {
  height: calc(100vh - 53px);
}
.desktop {
  width: 100%;
}
.mobileDevice {
  width: 100vw;
  /* AB#21537 — room for the bottom navigation bar on a phone. */
  padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
}
.spinner {
  border: 5px solid var(--accent);
  border-top-color: transparent;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}
.sidebar-slide-enter-to,
.sidebar-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
